import { io } from "socket.io-client";
import { getToken } from "../../common/environment_config.es6";
import { bearerSocketAuth } from "../utils/socketAuth";
import { apiGet, apiPost, apiPatch } from "../../common/http_service";
import { assetKey } from "../../common/utils";

// Build a middleware URL under the ideal-studio library root, mirroring
// sceneAudioService's base-url handling. The screen-training endpoints live at
// `${root}api/screen-training/...`.
export function buildTranscriptUrl(path, win = window) {
  let baseUrl = win.ideaLibraryRootUrl;
  if (!baseUrl) {
    throw new Error("ideaLibraryRootUrl not configured for transcripts");
  }
  if (!baseUrl.toLowerCase().startsWith("http")) {
    baseUrl = win.location.origin + baseUrl;
  }
  if (!baseUrl.endsWith("/")) {
    baseUrl += "/";
  }
  return `${baseUrl}api/${path}`;
}

function authHeaders() {
  const headers = {};
  const token = getToken && getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (window.tenantId) headers["Abp.TenantId"] = window.tenantId;
  return headers;
}

function resolveTrainingId(trainingId) {
  const id = trainingId || window.idealStudioTrainingId || "";
  if (!id) throw new Error("trainingId is missing for transcripts");
  return id;
}

export async function startTranscribe({
  videoUrl,
  boxId,
  navId,
  trainingId,
  language,
  durationSeconds,
}) {
  const url = buildTranscriptUrl("screen-training/transcribe");
  const body = { videoUrl, boxId, navId, trainingId: resolveTrainingId(trainingId), language };
  // Send the measured duration so the backend can enforce its transcribe limit
  // and run the credit pre-check without re-probing the video.
  if (typeof durationSeconds === "number" && durationSeconds > 0) {
    body.durationSeconds = durationSeconds;
  }
  const res = await apiPost(url, body, { headers: authHeaders() });
  if (!res.ok) throw new Error(`transcribe start failed (${res.status})`);
  return res.json();
}

export async function pollTranscribeJob(jobId, { intervalMs = 2500, timeoutMs = 300000 } = {}) {
  const url = buildTranscriptUrl(`screen-training/transcribe/${jobId}`);
  const deadline = Date.now() + timeoutMs;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const res = await apiGet(url, { headers: authHeaders() });
    if (!res.ok) throw new Error(`job poll failed (${res.status})`);
    const data = await res.json();
    if (data.status === "completed" || data.status === "failed") return data;
    if (Date.now() > deadline) throw new Error("transcription timed out");
    await new Promise((r) => setTimeout(r, intervalMs));
  }
}

export async function listTranscripts(trainingId) {
  const url = buildTranscriptUrl(
    `screen-training/transcripts?trainingId=${encodeURIComponent(resolveTrainingId(trainingId))}`
  );
  const res = await apiGet(url, { headers: authHeaders() });
  if (!res.ok) throw new Error(`list transcripts failed (${res.status})`);
  return res.json();
}

export function matchForBox(rows, box) {
  const url = box && box.state && box.state.url;
  if (!url || !Array.isArray(rows)) return null;
  return (
    rows.find((r) => r && r.metadata && assetKey(r.metadata.sourceUrl) === assetKey(url)) || null
  );
}

export async function updateTranscript(fileId, text) {
  const url = buildTranscriptUrl(`screen-training/transcripts/${fileId}`);
  const res = await apiPatch(url, { text }, { headers: authHeaders() });
  if (!res.ok) throw new Error(`update transcript failed (${res.status})`);
  return res.json();
}

// Start a text-to-speech job that renders `text` as narration OVER the given
// recorded video. Returns { jobId } immediately (HTTP 200); the video is
// produced asynchronously — poll pollTextToSpeechJob(jobId) for the result.
// When `enrichNarrationPromptLibraryId` is set, the backend first rewrites the
// text in that library style, then voices the enriched text; when omitted the
// text is voiced verbatim.
export async function startTextToSpeech({
  videoUrl,
  text,
  segments,
  trainingId,
  boxId,
  navId,
  sceneIndex,
  provider,
  voice,
  language,
  speed = 1.0,
  enrichNarrationPromptLibraryId,
}) {
  const url = buildTranscriptUrl("screen-training/text-to-speech");
  const body = {
    videoUrl,
    trainingId: resolveTrainingId(trainingId),
    boxId,
    navId,
    sceneIndex,
    provider,
    voice,
    language,
    speed,
  };
  // Exactly one of `segments` (time-aligned) or `text` (single continuous clip).
  // segments = [{ start, end (seconds), text }] straight from the transcript.
  // `end` lets the backend derive each inter-segment pause from the original
  // silence (next.start − this.end); dropping it forces a fixed fallback gap.
  const timed = Array.isArray(segments) && segments.length > 0;
  if (timed) {
    body.segments = segments.map((s) => ({ start: s.start, end: s.end, text: s.text }));
  } else {
    body.text = text;
    // Narration-style enrichment applies to the single-text flow only. Only
    // sent when a style is picked; absent → voiced verbatim.
    if (enrichNarrationPromptLibraryId != null && enrichNarrationPromptLibraryId !== "") {
      body.enrichNarrationPromptLibraryId = Number(enrichNarrationPromptLibraryId);
    }
  }
  const res = await apiPost(url, body, { headers: authHeaders() });
  if (!res.ok) throw new Error(`text-to-speech start failed (${res.status})`);
  return res.json();
}

// List the narration-enrichment styles for the "Anlatım Stili" dropdown. Each
// entry is a prompt-library row { id, name, description, category, sampleText }.
// The picked `id` is sent as `enrichNarrationPromptLibraryId`; omitting it voices
// the text verbatim. `lang` (tr/en/es) localizes `description`/`sampleText`
// (NOTE: `name` stays English — the UI shows `description` for the label).
// Lives on MW_API_URL (localhost:4000), NOT the ideal-studio library root.
export async function listNarrationStyles(lang = "tr") {
  const url = `${process.env.MW_API_URL}api/enrich-narration-prompt-library?lang=${encodeURIComponent(
    lang
  )}`;
  const res = await fetch(url, { headers: authHeaders() });
  if (!res.ok) throw new Error(`list narration styles failed (${res.status})`);
  const data = await res.json();
  return Array.isArray(data?.libraries) ? data.libraries : [];
}

// Poll a text-to-speech job until it reaches a terminal state. Resolves with
// the full job payload ({ jobId, status, progress, result, error }) when the
// status is "completed" or "failed". Kept as a socket fallback.
export async function pollTextToSpeechJob(jobId, { intervalMs = 2500, timeoutMs = 300000 } = {}) {
  const url = buildTranscriptUrl(`screen-training/text-to-speech/${jobId}`);
  const deadline = Date.now() + timeoutMs;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const res = await apiGet(url, { headers: authHeaders() });
    if (!res.ok) throw new Error(`tts job poll failed (${res.status})`);
    const data = await res.json();
    if (data.status === "completed" || data.status === "failed") return data;
    if (Date.now() > deadline) throw new Error("text-to-speech timed out");
    await new Promise((r) => setTimeout(r, intervalMs));
  }
}

// Backend Socket.IO root: ideaLibraryRootUrl minus any trailing `/api` (REST is
// under /api, the socket server runs at the root). Mirrors useSessionImageJobs.
function readSocketRoot() {
  const root = typeof window !== "undefined" ? window.ideaLibraryRootUrl : null;
  if (typeof root !== "string" || !root) return null;
  return root.replace(/\/+$/, "").replace(/\/api$/, "");
}

// Wait for a text-to-speech ("screen-guide-narration") job to finish. Primary
// channel is the per-training session socket (join_session → session_job_update
// / session_job_completed); HTTP polling is a fallback that races the socket so
// the result still lands if the socket can't connect. Resolves with a normalized
// { status: "completed"|"failed", result, error } for the matching jobId, and
// reports progress via onProgress(percent, status).
export function waitForTextToSpeechJob(jobId, { trainingId, onProgress, timeoutMs = 300000 } = {}) {
  const tid = resolveTrainingId(trainingId);
  const socketRoot = readSocketRoot();
  const token = getToken && getToken();

  return new Promise((resolve, reject) => {
    let settled = false;
    let socket = null;
    let pollTimer = null;
    let deadlineTimer = null;

    const cleanup = () => {
      if (deadlineTimer) clearTimeout(deadlineTimer);
      if (pollTimer) clearTimeout(pollTimer);
      if (socket) {
        try {
          socket.emit("leave_session", { trainingId: tid });
        } catch {
          /* best-effort */
        }
        socket.off();
        socket.disconnect();
        socket = null;
      }
    };
    const done = (fn, val) => {
      if (settled) return;
      settled = true;
      cleanup();
      fn(val);
    };

    // --- Socket (primary) ---
    if (socketRoot && token) {
      socket = io(socketRoot, {
        transports: ["websocket", "polling"],
        // Function form: a job wait can outlast the token it started with.
        auth: bearerSocketAuth(getToken),
        reconnection: true,
        reconnectionDelay: 500,
        reconnectionDelayMax: 5000,
      });
      const join = () => socket.emit("join_session", { trainingId: tid });
      socket.on("connect", join);

      const matches = (p) => p && (p.jobId || p.id) === jobId;
      socket.on("session_job_update", (p) => {
        if (!matches(p)) return;
        if (typeof p.progress === "number") onProgress?.(p.progress, p.status);
        if (p.status === "failed") {
          done(resolve, { status: "failed", result: null, error: p.error || null });
        }
      });
      socket.on("session_job_completed", (p) => {
        if (!matches(p)) return;
        const result =
          p.result ||
          (p.metadata && (p.metadata.silentVideoUrl || p.metadata.audioUrl) ? p.metadata : null);
        done(resolve, { status: "completed", result, error: null });
      });
    }

    // --- HTTP polling (fallback, races the socket) ---
    const deadline = Date.now() + timeoutMs;
    const poll = async () => {
      if (settled) return;
      try {
        const url = buildTranscriptUrl(`screen-training/text-to-speech/${jobId}`);
        const res = await apiGet(url, { headers: authHeaders() });
        if (res.ok) {
          const data = await res.json();
          if (typeof data.progress === "number") onProgress?.(data.progress, data.status);
          if (data.status === "completed" || data.status === "failed") {
            done(resolve, {
              status: data.status,
              result: data.result || null,
              error: data.error || null,
            });
            return;
          }
        }
      } catch {
        /* transient — keep polling until the deadline */
      }
      if (Date.now() > deadline) {
        done(reject, new Error("text-to-speech timed out"));
        return;
      }
      pollTimer = setTimeout(poll, 3000);
    };
    // Give the socket a short head start, then begin the fallback poll.
    pollTimer = setTimeout(poll, 3000);
    deadlineTimer = setTimeout(
      () => done(reject, new Error("text-to-speech timed out")),
      timeoutMs
    );
  });
}
